import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Clock, ArrowLeft, Tag, User, MessageCircle, Send, Share2, Linkedin, Twitter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '@/components/SEO';
import { logger } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (post) {
      fetchComments();
    }
  }, [post]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      logger.error('Failed to fetch post:', error);
      if (error.response?.status === 404) {
        navigate('/blog');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${post.id}/comments`);
      setComments(response.data);
    } catch (error) {
      logger.error('Failed to fetch comments:', error);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    if (!user) {
      toast.error('Please log in to comment');
      navigate('/login');
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(
        `${API}/blog/posts/${post.id}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      toast.success('Comment added!');
      setNewComment('');
      fetchComments();
    } catch (error) {
      logger.error('Failed to add comment:', error);
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    return formatDate(dateString);
  };

  const shareOnLinkedIn = () => {
    const url = window.location.href;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnTwitter = () => {
    const url = window.location.href;
    const text = post?.title || 'Check out this article on TabedLab';
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO 
        title={post.title}
        description={post.excerpt || post.content?.substring(0, 160)}
        keywords={post.tags?.join(', ')}
        url={`/blog/${post.slug}`}
        image={post.featured_image}
        type="article"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img 
              src="/tabedlab-logo.png" 
              alt="TabedLab Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              TabedLab
            </span>
          </div>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="back-to-blog-button"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Blog
          </Button>
        </div>
      </header>

      {/* Article */}
      <article className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Hero Image */}
          {post.featured_image && (
            <div className="aspect-video rounded-2xl overflow-hidden mb-8">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-full object-cover"
                data-testid="post-featured-image"
              />
            </div>
          )}

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-4">
            {post.categories?.map((cat) => (
              <button
                key={cat}
                onClick={() => navigate(`/blog?category=${cat}`)}
                className="px-3 py-1 text-sm bg-cyan-500/20 text-cyan-400 rounded-full hover:bg-cyan-500/30 transition-colors"
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="post-title">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-slate-400 mb-8 pb-8 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold">
                {post.author_name?.charAt(0) || 'A'}
              </div>
              <div>
                <p className="text-white font-medium">{post.author_name}</p>
                <p className="text-sm text-slate-500">{post.author_bio}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.reading_time} min read
              </span>
            </div>
            
            {/* Share Buttons */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-slate-500">Share:</span>
              <button
                onClick={shareOnLinkedIn}
                className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </button>
              <button
                onClick={shareOnTwitter}
                className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all"
                aria-label="Share on Twitter"
              >
                <Twitter className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none mb-12" data-testid="post-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({children}) => <h1 className="text-3xl font-bold text-white mt-8 mb-4">{children}</h1>,
                h2: ({children}) => <h2 className="text-2xl font-bold text-white mt-8 mb-4">{children}</h2>,
                h3: ({children}) => <h3 className="text-xl font-bold text-white mt-6 mb-3">{children}</h3>,
                p: ({children}) => <p className="text-slate-300 leading-relaxed mb-4">{children}</p>,
                ul: ({children}) => <ul className="list-disc list-inside text-slate-300 mb-4 space-y-2">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal list-inside text-slate-300 mb-4 space-y-2">{children}</ol>,
                li: ({children}) => <li className="text-slate-300">{children}</li>,
                a: ({href, children}) => <a href={href} className="text-cyan-400 hover:underline">{children}</a>,
                blockquote: ({children}) => <blockquote className="border-l-4 border-cyan-500 pl-4 italic text-slate-400 my-4">{children}</blockquote>,
                code: ({inline, children}) => inline 
                  ? <code className="bg-slate-800 px-2 py-0.5 rounded text-cyan-400 text-sm">{children}</code>
                  : <code className="block bg-slate-800 p-4 rounded-lg overflow-x-auto text-sm text-slate-300">{children}</code>,
                pre: ({children}) => <pre className="bg-slate-800 p-4 rounded-lg overflow-x-auto mb-4">{children}</pre>,
                table: ({children}) => <div className="overflow-x-auto mb-4"><table className="w-full border-collapse border border-slate-700">{children}</table></div>,
                th: ({children}) => <th className="border border-slate-700 bg-slate-800 px-4 py-2 text-left text-white">{children}</th>,
                td: ({children}) => <td className="border border-slate-700 px-4 py-2 text-slate-300">{children}</td>,
                hr: () => <hr className="border-slate-700 my-8" />,
                strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                em: ({children}) => <em className="text-slate-300">{children}</em>,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-12 pb-8 border-b border-slate-800">
              <Tag className="w-4 h-4 text-slate-500" />
              {post.tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => navigate(`/blog?tag=${tag}`)}
                  className="px-3 py-1 text-sm bg-slate-800 text-slate-400 rounded-full hover:text-white hover:bg-slate-700 transition-colors"
                >
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* Related Posts */}
          {post.related_posts?.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {post.related_posts.map((related) => (
                  <article
                    key={related.id}
                    className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover:border-cyan-500/30 transition-all cursor-pointer group"
                    onClick={() => navigate(`/blog/${related.slug}`)}
                  >
                    {related.featured_image && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={related.featured_image}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-white group-hover:text-cyan-400 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <p className="text-sm text-slate-500 mt-2">
                        {related.reading_time} min read
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* Comments Section */}
          <section className="bg-slate-800/30 border border-slate-700 rounded-2xl p-6" data-testid="comments-section">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-cyan-400" />
              Comments ({comments.length})
            </h2>

            {/* Comment Form */}
            <form onSubmit={handleSubmitComment} className="mb-8">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={user ? "Write a comment..." : "Please log in to comment"}
                disabled={!user}
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 mb-3 min-h-[100px]"
                data-testid="comment-textarea"
              />
              <div className="flex items-center justify-between">
                {!user && (
                  <p className="text-slate-500 text-sm">
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="text-cyan-400 hover:underline"
                    >
                      Log in
                    </button>
                    {' '}to join the discussion
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={!user || !newComment.trim() || submittingComment}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 ml-auto"
                  data-testid="submit-comment-button"
                >
                  {submittingComment ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Posting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Post Comment
                    </span>
                  )}
                </Button>
              </div>
            </form>

            {/* Comments List */}
            {comments.length === 0 ? (
              <p className="text-slate-500 text-center py-8">
                No comments yet. Be the first to share your thoughts!
              </p>
            ) : (
              <div className="space-y-6">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="flex gap-4 p-4 bg-slate-800/30 rounded-xl"
                    data-testid={`comment-${comment.id}`}
                  >
                    <div className="flex-shrink-0">
                      {comment.user_picture ? (
                        <img
                          src={comment.user_picture}
                          alt={comment.user_name}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                          {comment.user_name?.charAt(0) || 'U'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-white">{comment.user_name}</span>
                        <span className="text-slate-500 text-sm">
                          {formatCommentDate(comment.created_at)}
                        </span>
                      </div>
                      <p className="text-slate-300">{comment.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to Try AI-Powered Spectral Analysis?
          </h2>
          <p className="text-slate-400 mb-6">
            Upload your spectral data and get instant AI-powered insights.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-lg px-8 py-6"
              data-testid="post-cta-register"
            >
              Start Free Analysis
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/demo')}
              className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-lg px-8 py-6"
            >
              Try Demo First
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} TabedLab. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
